<script setup lang="ts">
import {
  ChevronDown,
  X,
} from 'lucide-vue-next';
import {
  computed, nextTick, ref, watch,
} from 'vue';

export interface SearchableSelectOption {
  label: string;
  value: string;
}

export interface SearchableSelectProps {
  disabled?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  id?: string;
  loading?: boolean;
  loadingText?: string;
  modelValue?: string;
  noOptionsText?: string;
  options?: SearchableSelectOption[];
  placeholder?: string;
}

const props = withDefaults(defineProps<SearchableSelectProps>(), {
  disabled: false,
  errorMessage: '',
  hasError: false,
  id: undefined,
  loading: false,
  loadingText: 'Carregando...',
  modelValue: '',
  noOptionsText: 'Nenhuma opção encontrada',
  options: () => [],
  placeholder: 'Digite para buscar ou criar novo',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const isOpen = ref(false);
const searchText = ref('');
const inputRef = ref<HTMLInputElement | null>(null);

const internalValue = computed({
  get: () => props.modelValue || '',
  set: (value: string) => emit('update:modelValue', value),
});

const filteredOptions = computed(() => {
  if (!searchText.value.trim()) return props.options;

  const search = searchText.value.toLowerCase().trim();
  return props.options.filter((option) => option.label.toLowerCase().includes(search)
    || option.value.toLowerCase().includes(search));
});

const isExistingOption = computed(() => {
  if (!searchText.value.trim()) return false;
  return props.options.some((option) => option.value.toLowerCase() === searchText.value.toLowerCase().trim()
    || option.label.toLowerCase() === searchText.value.toLowerCase().trim());
});

const showCreateNew = computed(() => searchText.value.trim()
  && filteredOptions.value.length === 0
  && !isExistingOption.value);

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    const option = props.options.find((opt) => opt.value === newValue);
    searchText.value = option ? option.label : newValue;
  } else {
    searchText.value = '';
  }
}, { immediate: true });

const handleInputFocus = () => {
  isOpen.value = true;
};

const handleInputBlur = () => {
  setTimeout(() => {
    isOpen.value = false;
  }, 200);
};

const handleDropdownToggle = () => {
  if (props.disabled || props.loading) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
};

const handleOptionSelect = (option: SearchableSelectOption) => {
  searchText.value = option.label;
  internalValue.value = option.value;
  isOpen.value = false;
  inputRef.value?.blur();
};

const handleCreateNew = () => {
  const newValue = searchText.value.trim();
  if (newValue) {
    internalValue.value = newValue;
    isOpen.value = false;
    inputRef.value?.blur();
  }
};

const handleClear = () => {
  searchText.value = '';
  internalValue.value = '';
  nextTick(() => {
    inputRef.value?.focus();
  });
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isOpen.value = false;
    inputRef.value?.blur();
  } else if (event.key === 'Enter' && showCreateNew.value) {
    event.preventDefault();
    handleCreateNew();
  }
};
</script>

<template>
  <div class="searchable-select" :class="{ 'is-disabled': disabled }">
    <div class="input-wrapper">
      <input
        :id="id"
        ref="inputRef"
        v-model="searchText"
        type="text"
        class="form-control"
        :class="{ 'is-open': isOpen, 'is-invalid': hasError }"
        :disabled="disabled || loading"
        :placeholder="loading ? loadingText : placeholder"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @keydown="handleKeydown"
      />

      <div class="input-actions">
        <button
          v-if="searchText && !disabled"
          type="button"
          class="btn-clear"
          @click="handleClear"
        >
          <X :size="16" />
        </button>

        <button
          type="button"
          class="dropdown-arrow"
          :class="{ 'is-open': isOpen }"
          :disabled="disabled || loading"
          @click="handleDropdownToggle"
        >
          <ChevronDown :size="16" />
        </button>
      </div>
    </div>

    <div v-if="isOpen && !disabled" class="dropdown-menu show">
      <div v-if="loading" class="dropdown-item disabled">
        <div class="d-flex align-items-center">
          <div class="spinner-border spinner-border-sm me-2" role="status" />
          {{ loadingText }}
        </div>
      </div>

      <template v-else>
        <button
          v-for="option in filteredOptions"
          :key="option.value"
          type="button"
          class="dropdown-item"
          @click="handleOptionSelect(option)"
        >
          {{ option.label }}
        </button>

        <button
          v-if="showCreateNew"
          type="button"
          class="dropdown-item create-new"
          @click="handleCreateNew"
        >
          <strong>Criar:</strong> "{{ searchText.trim() }}"
        </button>

        <div
          v-if="!showCreateNew && filteredOptions.length === 0 && searchText.trim()"
          class="dropdown-item disabled"
        >
          {{ noOptionsText }}
        </div>
      </template>
    </div>

    <div v-if="hasError && errorMessage" class="invalid-feedback d-block">
      {{ errorMessage }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.searchable-select {
  position: relative;

  &.is-disabled {
    opacity: 0.6;
  }
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-control {
  padding-right: 3rem;

  &.is-open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-color: transparent;
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  }
}

.input-actions {
  position: absolute;
  right: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  pointer-events: none;

  .btn-clear,
  .dropdown-arrow {
    pointer-events: auto;
  }
}

.btn-clear {
  background: none;
  border: none;
  padding: 0.25rem;
  color: var(--bs-secondary);
  cursor: pointer;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--bs-danger);
    background-color: var(--bs-light);
  }
}

.dropdown-arrow {
  background: none;
  border: none;
  color: var(--bs-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: var(--bs-light);
    color: var(--bs-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-open {
    transform: rotate(180deg);
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

  .dropdown-item {
    cursor: pointer;
    border: none;
    background: none;
    text-align: left;
    width: 100%;

    &:hover:not(.disabled) {
      background-color: var(--bs-light);
    }

    &.disabled {
      color: var(--bs-secondary);
      cursor: default;
    }

    &.create-new {
      border-top: 1px solid var(--bs-border-color);
      color: var(--bs-primary);

      &:hover {
        background-color: rgba(13, 110, 253, 0.1);
      }
    }
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
